import React from "react";
import { formatCurrencyToNearestTen } from "../../formatting/formatCurrency";
import { formatUnitsToNearestTen } from "../../formatting/formatKiloUnits";
import formatMass from "../../formatting/formatMass";
import tariffToFractionalCurrency from "../../formatting/tariffToFractionalCurrency";
import { MetricCardsRow, SiteMetricCard } from "./index";

interface Metrics {
  totalSites: number;
  totalAnnualDemand: number;
  averageTariff: number;
  isAnySitePvLease: boolean;
  totalEnergyProcuredCost: number;
  totalPpaSavings: number;
  emissionsAvoided: number;
  hideSavings?: boolean;
}

interface TenantSiteListMetricCardsProps {
  metrics: Metrics;
  summaryCurrencyCode: string;
}

export const TenantSiteListMetricCards: React.FC<
  TenantSiteListMetricCardsProps
> = ({ metrics, summaryCurrencyCode }) => {
  return (
    <MetricCardsRow>
      <SiteMetricCard
        title="Total sites"
        value={metrics.totalSites}
        color="grey"
        height="168px"
        width="100%"
        titleColor="#5486AD"
        dataTestId="total-sites"
      />
      <SiteMetricCard
        title="Total annual demand"
        value={formatUnitsToNearestTen(metrics.totalAnnualDemand, "kWh")}
        tooltipText={`The total estimated energy demand across all sites.`}
        tooltipPosition="left"
        color="white"
        height="168px"
        width="100%"
        titleColor="#5486AD"
        dataTestId="total-annual-demand"
      />
      {!metrics.isAnySitePvLease && (
        <>
          <SiteMetricCard
            title="Average tariff for solar energy"
            value={tariffToFractionalCurrency(
              metrics.averageTariff,
              summaryCurrencyCode
            )}
            tooltipText="The average tariff for procuring solar energy from InRange across your sites."
            tooltipPosition="left"
            color="white"
            height="168px"
            width="100%"
            titleColor="#5486AD"
            dataTestId="average-tariff"
          />
          <SiteMetricCard
            title="Total procured from InRange in year 1"
            value={formatCurrencyToNearestTen(
              metrics.totalEnergyProcuredCost,
              summaryCurrencyCode,
              0,
              false
            )}
            note="From on-site generation and the InRange network"
            tooltipText="The total amount for all energy procured across sites, given site-specific tariffs and energy demand. Includes energy procured behind the meter from an on-site installation, as well as energy imported from the InRange network."
            tooltipPosition="left"
            color="white"
            height="168px"
            width="100%"
            titleColor="#5486AD"
            dataTestId="total-energy-procured"
          />
        </>
      )}
      {!metrics.hideSavings && (
        <SiteMetricCard
          title="Total savings over PPA lengths"
          value={formatCurrencyToNearestTen(
            metrics.totalPpaSavings,
            summaryCurrencyCode,
            0,
            false
          )}
          tooltipText="The potential total savings from the energy procured from InRange over the lifetime of your sites' respective PPAs. Considers current market tariffs, energy inflation, and any relevant passthrough charges on a site-by-site basis."
          tooltipPosition="left"
          color="white"
          height="168px"
          width="100%"
          titleColor="#5486AD"
          dataTestId="total-savings"
        />
      )}
      <SiteMetricCard
        title="Total emissions avoided in year 1"
        value={formatMass(metrics.emissionsAvoided) + " CO2e"}
        tooltipText="The total emissions you will have avoided by consuming solar energy across sites. The CO2e multiplier per MWh is based on the annual carbon intensity of the grid at each site location."
        tooltipPosition="left"
        color="white"
        height="168px"
        width="100%"
        titleColor="#5486AD"
        dataTestId="total-emissions-avoided"
      />
    </MetricCardsRow>
  );
};
